import { bindContent, contentCatalog } from "../content/catalog"
import { REGISTRATION } from "../content/appearance"
import { Bauldron, ItemTypes, Key } from "game-common/item/item_type"

type Mutable<T> = { -readonly [K in keyof T]: Mutable<T[K]> }
const authored = <T>(value: T): Mutable<T> => JSON.parse(JSON.stringify(value))

export let HumanoidRaceTypes = REGISTRATION.HumanoidRaceTypes

export let RaceTypes = REGISTRATION.RaceTypes

export type Race = (typeof RaceTypes)[number]

export const SexTypes = ["male", "female"] as const

export type Sex = (typeof SexTypes)[number]

export let RaceSexes: Record<Race, Sex[]> = authored(REGISTRATION.RaceSexes)

/**
 * The colour vocabulary, as an array so it exists at runtime.
 *
 * It used to be a bare union, which meant nothing could enumerate it: the sprite lab kept a
 * hand-typed copy for its colour picker, and the importer had no way to check that a colour it
 * was about to write was real. A `const` array gives the identical union plus a list, the same
 * shape `RaceTypes` and `HairTypes` already use.
 *
 * This is the vocabulary, not the art; it is authored in content/game/vocabularies.json. Which
 * colours a component comes in is derived from the registry (see below); adding a NAME to the
 * vocabulary is still a decision someone makes.
 */
export let ColorTypes: string[] = contentCatalog.source.vocabularies.colors

export type Color = (typeof ColorTypes)[number]

import { SPRITE_COLORS } from "../content/appearance"

/**
 * The colour tables below are DERIVED from the sprite registry, not written here.
 *
 * Which colours a component comes in is a fact about the art on disk, and keeping it by hand is
 * what made importing a new colour a code change in three places. Each table is the registry
 * entries' `colors` (content/game/sprites/registry.json), read back out of the compiled content
 * catalog's `spriteColors`; edit the registry (or use the sprite lab) and run `yarn compile:lpc`,
 * which checks the registry and then compiles content.
 *
 * Types and eligibility are authored in content/game/sprites/definitions.json and arrive through
 * the same catalog (`REGISTRATION`).
 */

/** Widen one slot's readonly literal tuples to the `Color[]` every caller already expects. */
const derive = <T extends Record<string, readonly string[]>>(table: T): { [K in keyof T]: Color[] } =>
    Object.keys(table).reduce((out, key) => {
        out[key] = [...table[key]]
        return out
    }, {} as any)

export let RaceColors: Record<Race, Color[]> = derive(SPRITE_COLORS.body)

export let EyeTypes = REGISTRATION.EyeTypes

export type Eyes = (typeof EyeTypes)[number]

export let EyesColors: Record<Eyes, Color[]> = derive(SPRITE_COLORS.eyes)

export let NoseTypes = REGISTRATION.NoseTypes

export type Nose = (typeof NoseTypes)[number] | "none"

const commonNoseColor: Color[] = ["white", "tan", "gray"]

export let NoseColors: Record<Nose, Color[]> = {
    ...derive(SPRITE_COLORS.nose),
    // "none" is a Nose, not a component: it means the slot is empty, and the
    // forge returns no sheets for it. The art has nothing to say about it.
    none: [],
}

// todo
export let RaceNoses: Partial<Record<Race, Nose[]>> = authored(REGISTRATION.RaceNoses)

export let RaceEyes: Partial<Record<Race, Eyes[]>> = authored(REGISTRATION.RaceEyes)

export let WeaponTypes = REGISTRATION.WeaponTypes

export type Weapon = (typeof WeaponTypes)[number]

const commonWeapons: Weapon[] = ["spear", "bow", "staff", "axe", "warhammer", "none"]

export let WeaponAction: Record<Weapon, ActionType> = authored(REGISTRATION.WeaponAction)

export let WeaponAmmo: Partial<Record<Weapon, ItemTypes>> = authored(REGISTRATION.WeaponAmmo)

export let WeaponColors: Record<Weapon, Color[]> = derive(SPRITE_COLORS.weapon)

export let RaceWeapons: Partial<Record<Race, Weapon[]>> = authored(REGISTRATION.RaceWeapons)

export let DefaultAttackActionType: Partial<Record<Race, ActionType>> = authored(REGISTRATION.DefaultAttackActionType)

export let HairTypes = REGISTRATION.HairTypes

export type Hair = (typeof HairTypes)[number]

const commonHairColor: Color[] = [
    "white",
    "blonde",
    "gold",
    "green",
    "blue",
    "red",
    "orange",
    "purple",
    "brown",
    "lightbrown",
    "gray",
    "black",
]

export let HairColors: Record<Hair, Color[]> = derive(SPRITE_COLORS.hair)

export let SexHair: Record<Sex, Hair[]> = authored(REGISTRATION.SexHair)

export let PantsTypes = REGISTRATION.PantsTypes

export type Pants = (typeof PantsTypes)[number]

export let SexPants: Record<Sex, Pants[]> = authored(REGISTRATION.SexPants)

export let PantsColors: Record<Pants, Color[]> = derive(SPRITE_COLORS.pants)

export let ShirtTypes = REGISTRATION.ShirtTypes

export type Shirt = (typeof ShirtTypes)[number]

export let SexShirts: Record<Sex, Shirt[]> = authored(REGISTRATION.SexShirts)

const commonShirtColors: Color[] = ["black", "blue", "green", "maroon", "purple", "red", "teal", "white", "yellow"]

export let ShirtColors: Record<Shirt, Color[]> = derive(SPRITE_COLORS.shirt)

export let ArmorTypes = REGISTRATION.ArmorTypes

export type Armor = (typeof ArmorTypes)[number]

export let ArmorColors: Record<Armor, Color[]> = derive(SPRITE_COLORS.armor)

export let ShoeTypes = REGISTRATION.ShoeTypes

export type Shoe = (typeof ShoeTypes)[number]

export let SexShoes: Record<Sex, Shoe[]> = authored(REGISTRATION.SexShoes)

export let ShoeColors: Record<Shoe, Color[]> = derive(SPRITE_COLORS.shoes)

export let ShieldTypes = REGISTRATION.ShieldTypes

export type Shield = (typeof ShieldTypes)[number]

export let ShieldColors: Record<Shield, Color[]> = derive(SPRITE_COLORS.shield)

export let CapeTypes = REGISTRATION.CapeTypes

export type Cape = (typeof CapeTypes)[number]

export let SexCapes: Record<Sex, Cape[]> = authored(REGISTRATION.SexCapes)

export let CapeColors: Record<Cape, Color[]> = derive(SPRITE_COLORS.cape)

export const KeyColors: Record<Key, Color[]> = {
    castlekey: ["gold"],
}

export let BauldronColors: Record<Bauldron, Color[]> = derive(SPRITE_COLORS.bauldron)
export type ActionType =
    | "walk"
    | "idle"
    | "cast"
    | "thrust"
    | "slash"
    | "shoot"
    | "hurt"
    | "activate"
    | "deactivate"
    | "active"
    | "inactive"


/**
 * Guards on the generated colour data. Both are compile-time only; neither runs.
 *
 * Without them "derived from data" would mean "unchecked", which is worse than the hand-written
 * tables it replaced.
 */

/**
 * 1. No value may be a colour the game does not have a name for. A registry that imported
 *    upstream's `chartreuse` fails the build here, naming it, rather than resolving to a path
 *    nothing renders.
 */
const _spriteColorsAreRealColors: Record<string, Record<string, readonly Color[]>> = SPRITE_COLORS
void _spriteColorsAreRealColors

/**
 * 2. Every declared component must have an entry. This is the exhaustiveness `Record<Race, ...>`
 *    used to give for free when the table was written out by hand: drop a race from the registry
 *    and the build fails instead of that race rendering nothing.
 */
const _everyComponentHasColours: [
    Record<Race, unknown>,
    Record<Hair, unknown>,
    Record<Weapon, unknown>,
    Record<Pants, unknown>,
    Record<Shirt, unknown>,
    Record<Armor, unknown>,
    Record<Shoe, unknown>,
    Record<Shield, unknown>,
    Record<Cape, unknown>,
    Record<Bauldron, unknown>,
] = [
    SPRITE_COLORS.body,
    SPRITE_COLORS.hair,
    SPRITE_COLORS.weapon,
    SPRITE_COLORS.pants,
    SPRITE_COLORS.shirt,
    SPRITE_COLORS.armor,
    SPRITE_COLORS.shoes,
    SPRITE_COLORS.shield,
    SPRITE_COLORS.cape,
    SPRITE_COLORS.bauldron,
]
void _everyComponentHasColours

/**
 * The literal-typed table, for code that wants the exact colours a component offers rather than
 * `Color[]`. `ColorsOf<"hair", "long">` is `"white" | "blonde" | ... | "black"`, checked at the
 * call site; the `Record<X, Color[]>` tables above stay as they are for every existing caller.
 */
export { SPRITE_COLORS }
export type ColorsOf<
    S extends keyof typeof SPRITE_COLORS,
    C extends keyof (typeof SPRITE_COLORS)[S],
> = (typeof SPRITE_COLORS)[S][C] extends readonly (infer T)[] ? T : never

// Rebuild the complete table group during preparation; activation only assigns bindings.
bindContent(catalog => ({
    HumanoidRaceTypes: catalog.source.sprites.registration.HumanoidRaceTypes,
    RaceTypes: catalog.source.sprites.registration.RaceTypes,
    RaceSexes: authored(catalog.source.sprites.registration.RaceSexes),
    ColorTypes: catalog.source.vocabularies.colors,
    RaceColors: derive(catalog.spriteColors.body),
    EyeTypes: catalog.source.sprites.registration.EyeTypes,
    EyesColors: derive(catalog.spriteColors.eyes),
    NoseTypes: catalog.source.sprites.registration.NoseTypes,
    RaceNoses: authored(catalog.source.sprites.registration.RaceNoses),
    RaceEyes: authored(catalog.source.sprites.registration.RaceEyes),
    WeaponTypes: catalog.source.sprites.registration.WeaponTypes,
    WeaponAction: authored(catalog.source.sprites.registration.WeaponAction),
    WeaponAmmo: authored(catalog.source.sprites.registration.WeaponAmmo),
    WeaponColors: derive(catalog.spriteColors.weapon),
    RaceWeapons: authored(catalog.source.sprites.registration.RaceWeapons),
    DefaultAttackActionType: authored(catalog.source.sprites.registration.DefaultAttackActionType),
    HairTypes: catalog.source.sprites.registration.HairTypes,
    HairColors: derive(catalog.spriteColors.hair),
    SexHair: authored(catalog.source.sprites.registration.SexHair),
    PantsTypes: catalog.source.sprites.registration.PantsTypes,
    SexPants: authored(catalog.source.sprites.registration.SexPants),
    PantsColors: derive(catalog.spriteColors.pants),
    ShirtTypes: catalog.source.sprites.registration.ShirtTypes,
    SexShirts: authored(catalog.source.sprites.registration.SexShirts),
    ShirtColors: derive(catalog.spriteColors.shirt),
    ArmorTypes: catalog.source.sprites.registration.ArmorTypes,
    ArmorColors: derive(catalog.spriteColors.armor),
    ShoeTypes: catalog.source.sprites.registration.ShoeTypes,
    SexShoes: authored(catalog.source.sprites.registration.SexShoes),
    ShoeColors: derive(catalog.spriteColors.shoes),
    ShieldTypes: catalog.source.sprites.registration.ShieldTypes,
    ShieldColors: derive(catalog.spriteColors.shield),
    CapeTypes: catalog.source.sprites.registration.CapeTypes,
    SexCapes: authored(catalog.source.sprites.registration.SexCapes),
    CapeColors: derive(catalog.spriteColors.cape),
    BauldronColors: derive(catalog.spriteColors.bauldron),
    NoseColors: { ...derive(catalog.spriteColors.nose), none: [] },
}), next => {
    HumanoidRaceTypes = next.HumanoidRaceTypes
    RaceTypes = next.RaceTypes
    RaceSexes = next.RaceSexes
    ColorTypes = next.ColorTypes
    RaceColors = next.RaceColors
    EyeTypes = next.EyeTypes
    EyesColors = next.EyesColors
    NoseTypes = next.NoseTypes
    RaceNoses = next.RaceNoses
    RaceEyes = next.RaceEyes
    WeaponTypes = next.WeaponTypes
    WeaponAction = next.WeaponAction
    WeaponAmmo = next.WeaponAmmo
    WeaponColors = next.WeaponColors
    RaceWeapons = next.RaceWeapons
    DefaultAttackActionType = next.DefaultAttackActionType
    HairTypes = next.HairTypes
    HairColors = next.HairColors
    SexHair = next.SexHair
    PantsTypes = next.PantsTypes
    SexPants = next.SexPants
    PantsColors = next.PantsColors
    ShirtTypes = next.ShirtTypes
    SexShirts = next.SexShirts
    ShirtColors = next.ShirtColors
    ArmorTypes = next.ArmorTypes
    ArmorColors = next.ArmorColors
    ShoeTypes = next.ShoeTypes
    SexShoes = next.SexShoes
    ShoeColors = next.ShoeColors
    ShieldTypes = next.ShieldTypes
    ShieldColors = next.ShieldColors
    CapeTypes = next.CapeTypes
    SexCapes = next.SexCapes
    CapeColors = next.CapeColors
    BauldronColors = next.BauldronColors
    NoseColors = next.NoseColors
})
